import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getCurrentUser } from "@/lib/authUser";
import { requestWithAuthRecovery } from "@/lib/authRequest";

interface UseLeadCountReturn {
  count: number;
  loading: boolean;
  canAddLead: boolean;
  refetch: () => Promise<void>;
}

export const useLeadCount = (limit?: number): UseLeadCountReturn => {
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchCount = async () => {
    try {
      const user = await getCurrentUser();
      
      if (!user) {
        setCount(0);
        setLoading(false);
        return;
      }

      // Count ALL personal leads regardless of status to enforce total limit
      const { count: leadCount, error } = await requestWithAuthRecovery(() =>
        supabase
          .from("leads")
          .select("*", { count: "exact", head: true })
          .eq("user_id", user.id)
          .is("group_id", null),
      );

      if (error) {
        console.error("Error fetching lead count:", error);
      } else {
        setCount(leadCount || 0);
      }
    } catch (error) {
      console.error("Error in fetchCount:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCount();

    // Subscribe to real-time changes
    const channel = supabase
      .channel("lead-count-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "leads",
        },
        () => {
          fetchCount();
        }
      )
      .subscribe();

    // Refresh count when tab becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchCount();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      supabase.removeChannel(channel);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // FULL ACCESS MODE: No lead limits
  // TODO: Re-enable paywalls when ready - original logic:
  // const canAddLead = limit ? count < limit : true;
  const canAddLead = true;

  return {
    count,
    loading,
    canAddLead,
    refetch: fetchCount,
  };
};
